// @flow

import { selectPricingInfos } from '@gandi/billing';
import type { DecoratedProduct } from '@gandi/cart';
import type { ApplicationSummary } from '@gandi/redux-resource-simplehosting';
import { useResourceFetch } from '@gandi/resource/react';

import { useAppSelector } from 'src/hooks/useAppSelector';
import {
  shsPricesResource,
  legacySizes,
  webhostingSizes,
} from 'src/modules/simplehosting/prices';

import type {
  LegacySize,
  PricesResourceType,
  SimplehostingPriceSummary,
  WebhostingSize,
} from '../../modules/simplehosting/prices';

export type HostingPlan = 'yearly' | 'monthly';

export function useSimplehostingPrices(
  process: DecoratedProduct['process'],
): ReturnType<typeof useResourceFetch<PricesResourceType>> {
  const { sharingId, ...pricingInfos } = useAppSelector(selectPricingInfos);

  const params = {
    ...pricingInfos,
    process,
    sharing_id: sharingId,
  };

  return useResourceFetch(shsPricesResource, params);
}

const legacyToCurrentSizes: { +[key: LegacySize]: WebhostingSize } = {
  s: 'starter',
  m: 'advanced',
  l: 'pro',
  xl: 'business',
};

function getIncludedSizes(
  requirement: ?LegacySize,
): $ReadOnlyArray<WebhostingSize> {
  if (!requirement) {
    return webhostingSizes;
  }

  const filteredSizes = legacySizes.slice(
    legacySizes.findIndex(size => size === requirement),
  );
  return filteredSizes.map(s => legacyToCurrentSizes[s]);
}

export function usePlanPrices(
  plan: HostingPlan,
  application?: ?ApplicationSummary,
): void | Array<SimplehostingPriceSummary> {
  const { summaries, isLoaded: instancesPricesLoaded } =
    useSimplehostingPrices('create');

  if (!instancesPricesLoaded) return undefined;

  // the catalog set yearly with
  // - min_duration = 12
  // - max_duration = 12
  // - duration_unit = 'month'
  // and monthly with
  // - min_duration = 1
  // - max_duration = 1
  // - duration_unit = 'month'
  const plan_min_duration = plan === 'monthly' ? 1 : 12;

  const sizesToInclude = getIncludedSizes(
    // $FlowFixMe[incompatible-call]
    application?.requirements?.[0].instance_minimum_size,
  );

  return summaries
    ?.filter(price => price.min_duration === plan_min_duration)
    .filter(price => sizesToInclude.includes(price.product.name))
    .sort((a, b) => a.price_before_taxes - b.price_before_taxes); // sort by price
}

export function hasEssentialOffer(
  offers: void | Array<SimplehostingPriceSummary>,
): void | boolean {
  return offers?.some(p => p.product.name === 'essential');
}
